import { z } from 'zod';

// ─── Import onboarding data from BQ ─────────────────────────────────
// Admin → Settings → System refreshes the selected tenants' Nexus onboarding records from the
// onboarding BigQuery tables. It reads BigQuery and writes Postgres only.

/** Well above the number of organizations; keeps one Pub/Sub message and one run bounded. */
export const ONBOARDING_IMPORT_MAX_TENANTS = 500;

/** How long the run's progress and report stay pollable after the last worker update. */
export const ONBOARDING_IMPORT_STATUS_TTL_SECONDS = 60 * 60;

// ─── Run lease ──────────────────────────────────────────────────────
// One import at a time. The API claims the lease when it starts a run; the worker renews
// `heartbeatAt` with a compare-and-swap before each tenant and right before each Postgres write, and
// stops the moment the swap fails. The API takes over only a finished run or a stale heartbeat, never
// on status expiry alone, and a worker that resumes after a takeover writes nothing more.

/** Cache key, as `new CacheKey(ONBOARDING_IMPORT_LEASE_MODULE).generate(ONBOARDING_IMPORT_LEASE_NAME)`. */
export const ONBOARDING_IMPORT_LEASE_MODULE = 'onboarding-import';
export const ONBOARDING_IMPORT_LEASE_NAME = 'active-run';
/** Well past one tenant's sync; a run silent for longer is treated as abandoned. */
export const ONBOARDING_IMPORT_LEASE_STALE_MS = 5 * 60_000;
export const ONBOARDING_IMPORT_LEASE_TTL_SECONDS = 24 * 60 * 60;

export const OnboardingImportLeaseSchema = z.object({
    correlationId: z.string(),
    userId: z.string(),
    claimedAt: z.number(),
    heartbeatAt: z.number(),
});

export type OnboardingImportLease = z.infer<typeof OnboardingImportLeaseSchema>;

export function isOnboardingImportLeaseStale(lease: OnboardingImportLease, now: number): boolean {
    return now - lease.heartbeatAt > ONBOARDING_IMPORT_LEASE_STALE_MS;
}

/** Always an explicit list: an empty selection is rejected, never read as "every tenant". */
export const OnboardingImportTenantIdsSchema = z
    .array(z.string().cuid2())
    .min(1, 'Select at least one tenant')
    .max(ONBOARDING_IMPORT_MAX_TENANTS, `Select at most ${ONBOARDING_IMPORT_MAX_TENANTS} tenants`)
    .refine((ids) => new Set(ids).size === ids.length, 'Each tenant can be selected only once');

export const OnboardingImportRequestSchema = z.object({ tenantIds: OnboardingImportTenantIdsSchema }).strict();

export type OnboardingImportRequest = z.infer<typeof OnboardingImportRequestSchema>;

/** A live organization with a customer partition, the only tenants the import can sync. */
export const OnboardingImportTenantSchema = z.object({
    id: z.string(),
    name: z.string(),
    customerPartition: z.string(),
});

export type OnboardingImportTenant = z.infer<typeof OnboardingImportTenantSchema>;

export const OnboardingImportOverviewSchema = z.object({
    tenants: z.array(OnboardingImportTenantSchema),
    /** A run that is still going. Only the admin who started it can follow its progress. */
    activeRun: z.object({ correlationId: z.string().uuid(), isOwnRun: z.boolean() }).nullable(),
});

export type OnboardingImportOverview = z.infer<typeof OnboardingImportOverviewSchema>;

export const OnboardingImportOutcomes = {
    SUCCESS: 'success',
    PARTIAL: 'partial',
    FAILED: 'failed',
} as const;

export type OnboardingImportOutcome = (typeof OnboardingImportOutcomes)[keyof typeof OnboardingImportOutcomes];

/** A synced tenant whose BigQuery customer has no clients_params row; its setup was left as it is. */
export const ONBOARDING_IMPORT_SETUP_MISSING = 'SETUP_MISSING';
/** The tenant's sync threw; the detail carries the error message. */
export const ONBOARDING_IMPORT_ERROR = 'ERROR';

export const OnboardingImportTenantResultSchema = z.discriminatedUnion('status', [
    z.object({
        tenantId: z.string(),
        status: z.literal('synced'),
        change: z.enum(['created', 'updated', 'unchanged']),
    }),
    z.object({
        tenantId: z.string(),
        status: z.literal('failed'),
        /** A sync skip reason, SETUP_MISSING or ERROR. */
        reason: z.string(),
        detail: z.string(),
    }),
]);

export type OnboardingImportTenantResult = z.infer<typeof OnboardingImportTenantResultSchema>;

export const OnboardingImportProgressSchema = z.object({
    done: z.number().int().nonnegative(),
    total: z.number().int().positive(),
});

export type OnboardingImportProgress = z.infer<typeof OnboardingImportProgressSchema>;

export const OnboardingImportReportSchema = z.object({
    outcome: z.enum([OnboardingImportOutcomes.SUCCESS, OnboardingImportOutcomes.PARTIAL, OnboardingImportOutcomes.FAILED]),
    total: z.number().int().positive(),
    synced: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    tenants: z.array(OnboardingImportTenantResultSchema),
});

export type OnboardingImportReport = z.infer<typeof OnboardingImportReportSchema>;

/** Success when every tenant synced, failed when none did, partial otherwise. */
export function toOnboardingImportOutcome(synced: number, total: number): OnboardingImportOutcome {
    if (synced === total) return OnboardingImportOutcomes.SUCCESS;
    if (synced === 0) return OnboardingImportOutcomes.FAILED;
    return OnboardingImportOutcomes.PARTIAL;
}
